import { useCallback, useEffect, useMemo, useRef } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCalendarXmark,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { useEventQr, useLogEventQrScan } from "../../hooks/useEventQr"
import { usePlayerEventQueue } from "../../hooks/usePlayerEventQueue"
import { getEventQrPlayerEventsBatchPath } from "../../api/eventQr"
import { getTrackingIds } from "../../utils/visitor"
import { eventQrStatuses } from "../../utils/eventQr"
import { scanSources } from "../../constants"
import VideoPlayer from "../EducationalVideos/VideoPlayer"
import Countdown from "./Countdown"
import EventFeedbackForm from "./EventFeedbackForm"

const Header = ({ eventName, subtitle }) => (
  <header className="bg-primary text-white text-center py-8 px-4">
    <div className="border border-gray-600 px-3 py-1 inline-block rounded-full uppercase text-[10px] md:text-xs text-[#60A5FA] font-medium tracking-wide backdrop-blur-sm bg-white/10 mb-3">
      Educational Videos
    </div>
    <h1 className="text-2xl md:text-4xl font-extrabold tracking-wide mb-3">
      {eventName || "Expert Radiology"}
    </h1>
    {subtitle && (
      <p className="text-[11px] md:text-sm leading-relaxed text-gray-400 font-medium max-w-xl mx-auto whitespace-pre-line">
        {subtitle}
      </p>
    )}
  </header>
)

const Message = ({ title, children }) => (
  <div className="flex flex-col items-center justify-center text-center min-h-60 px-4 py-16">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <FontAwesomeIcon
        className="w-6 h-6 text-gray-500"
        icon={faCalendarXmark}
      />
    </div>
    <h2 className="text-xl font-bold text-primary mb-2">{title}</h2>
    <p className="text-gray-500 max-w-md leading-relaxed">{children}</p>
  </div>
)

const Footer = () => (
  <footer className="bg-primary text-white text-center py-8 px-4">
    <div className="flex items-center justify-center gap-4 mb-6">
      <img alt="Expert Radiology" className="h-8" src="/logo.png" />
      <div className="w-px h-6 bg-gray-600"></div>
      <span className="text-gray-400 text-xs font-bold italic font-serif">
        PrecisionPlus v3™
      </span>
    </div>
    <p className="text-gray-500 text-xs mb-4 max-w-xl mx-auto leading-relaxed">
      These videos are for educational purposes only and do not constitute
      medical advice. Always consult your treating physician regarding your
      specific condition and treatment options.
    </p>
    <p className="text-xs mb-4">
      <a
        className="text-blue-500 hover:text-blue-300"
        href="https://www.expertradiology.com"
      >
        expertradiology.com
      </a>
      <span className="text-gray-400 mx-2">·</span>
      <a className="text-blue-500 hover:text-blue-300" href="tel:4159002000">
        415-900-2000
      </a>
    </p>
    <p className="text-[10px] text-gray-400/40">
      © {new Date().getFullYear()} Expert Radiology Management Services, LLC.
      All rights reserved.
    </p>
  </footer>
)

/*
  Public page behind an event QR code (conferences, trade shows). Tied to no
  report, patient or issuer: everything it loads and logs goes through
  /event-qr, never the report endpoints.

  The API decides the state (scheduled / live / ended) from the event's dates,
  and only sends videos while the event is live.
*/
const EventQrPage = () => {
  const { publicUuid } = useParams()
  const [searchParams] = useSearchParams()
  const {
    data: event,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useEventQr(publicUuid)
  const { mutate: logScan } = useLogEventQrScan()

  // Admin preview opens (?source=preview) render the same page and track
  // nothing.
  const isPreview = searchParams.get("source") === scanSources.PREVIEW
  const isLive = event?.status === eventQrStatuses.LIVE

  const trackingIds = useMemo(() => getTrackingIds(), [])
  const scanLoggedRef = useRef(false)

  // The scan. Only once the event is live, so a countdown visit isn't counted.
  useEffect(() => {
    if (isPreview || !isLive || scanLoggedRef.current) return
    scanLoggedRef.current = true
    logScan({ publicUuid, ...trackingIds })
  }, [isPreview, isLive, publicUuid, trackingIds, logScan])

  const enqueuePlayerEvent = usePlayerEventQueue({
    batchPath: getEventQrPlayerEventsBatchPath(publicUuid),
    enabled: !isPreview && isLive,
  })

  const handleCountdownDone = useCallback(() => refetch(), [refetch])

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-60">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading videos...</p>
          </div>
        </div>
      )
    }

    if (error || !event) {
      return (
        <Message title="This page isn't available">
          The link may be wrong, or this event is no longer running.
        </Message>
      )
    }

    if (event.status === eventQrStatuses.ENDED) {
      return (
        <Message title="This event has ended">
          Thank you for your interest. These videos are no longer available
          through this code.
        </Message>
      )
    }

    if (event.status === eventQrStatuses.SCHEDULED) {
      return (
        <div className="bg-primary pb-14 px-4 text-center">
          <p className="text-gray-300 text-sm mb-6">
            Videos will be available in
          </p>
          <Countdown
            key={dataUpdatedAt}
            fetchedAt={dataUpdatedAt}
            serverTime={event.serverTime}
            startsAt={event.startsAt}
            onDone={handleCountdownDone}
          />
          <p className="text-gray-400 text-xs mt-6">
            {DateTime.fromISO(event.startsAt).toFormat(
              "cccc, LLL d, yyyy 'at' h:mm a"
            )}
          </p>
        </div>
      )
    }

    return (
      <>
        <div className="py-12 px-4 max-w-6xl mx-auto">
          <div className="flex items-start gap-3 bg-blue-50/60 border border-blue-100 rounded-xl px-5 py-4 max-w-3xl mx-auto mb-8">
            <FontAwesomeIcon
              className="w-4 h-4 text-blue-600 shrink-0 mt-1.5"
              icon={faCircleInfo}
            />
            <p className="text-gray-500 leading-relaxed text-left font-semibold">
              The information provided below is for educational purposes only
              and is not intended to provide treatment guidelines, medical
              advice, or treatment recommendations.
            </p>
          </div>

          <div className="grid gap-8 md:gap-12">
            {event.videos.map((video, index) => (
              <div
                key={video.videoId}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="aspect-video w-full">
                  <VideoPlayer
                    enabled={!isPreview}
                    enqueueEvent={enqueuePlayerEvent}
                    source={scanSources.QR}
                    title={video.title || `Educational Video ${index + 1}`}
                    trackingIds={trackingIds}
                    video={{
                      VideoId: video.videoId,
                      VideoTitle: video.title,
                      VideoLink: video.videoUrl,
                    }}
                  />
                </div>
                {video.title && (
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {video.title}
                    </h3>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <EventFeedbackForm
          isPreview={isPreview}
          publicUuid={publicUuid}
          trackingIds={trackingIds}
        />
      </>
    )
  }

  return (
    <div className="-my-2">
      {isPreview && (
        <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 text-center">
          <p className="text-xs text-amber-900">
            <span className="font-bold uppercase tracking-wider">Preview</span>
            <span className="mx-2 text-amber-500">·</span>
            Internal view of the event page. Nothing is tracked and feedback
            cannot be submitted.
          </p>
        </div>
      )}

      {/* The admin's description when there is one, in every state; otherwise
          a generic line once the videos are showing. */}
      <Header
        eventName={event?.eventName}
        subtitle={
          event?.description ||
          (isLive
            ? "Short videos that explain common findings in plain language."
            : null)
        }
      />

      {renderBody()}

      <Footer />
    </div>
  )
}

export default EventQrPage
